import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { mergeDuplicateCities } from '../../../api/quarantine.api';

// --- Types for the Modal Data ---

interface CityDto {
  id: string;
  name: string;
  stateName?: string;
  isConfirmed: boolean;
}

interface DependentCityDto extends CityDto {
  foundIn: string[];
}

export interface SuggestedCityGroup {
  mostCorrectEntry: CityDto;
  otherEntries: DependentCityDto[];
}

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  groups: SuggestedCityGroup[];
  totalGroups: number;
  isLoading: boolean;
  error: string | null;
  onRefresh: () => void; // Function to refresh data after deletion
  token: string;
}

/**
 * A modal component to display groups of similar cities with suggestions and merge functionality.
 */
function SimilarCityGroupsModal({ isOpen, onClose, groups, totalGroups, isLoading, error, onRefresh, token }: ModalProps) {
  const [isMerging, setIsMerging] = useState<string | null>(null); // Tracks which single group is merging
  const [isMergingAll, setIsMergingAll] = useState(false);

  const handleMergeGroup = async (group: SuggestedCityGroup) => {
    if (!window.confirm(`Are you sure you want to merge ${group.otherEntries.length} duplicates into "${group.mostCorrectEntry.name}"? This action cannot be undone.`)) {
      return;
    }

    setIsMerging(group.mostCorrectEntry.id);
    try {
      const payload = [{
        correctCityId: group.mostCorrectEntry.id,
        duplicateCityIds: group.otherEntries.map(e => e.id),
      }];
      await mergeDuplicateCities(payload, token);
      onRefresh(); // Refresh the data to show the changes
    } catch (err: any) {
      alert(`Error merging group: ${err.message}`);
    } finally {
      setIsMerging(null);
    }
  };

  const handleMergeAll = async () => {
    if (!window.confirm(`Are you sure you want to merge all ${totalGroups} duplicate groups shown? This will affect hundreds of records and cannot be undone.`)) {
      return;
    }

    setIsMergingAll(true);
    try {
      const payload = groups.map(group => ({
        correctCityId: group.mostCorrectEntry.id,
        duplicateCityIds: group.otherEntries.map(e => e.id),
      }));
      await mergeDuplicateCities(payload, token);
      onRefresh(); // Refresh the data
      onClose();   // Close the modal on full success
    } catch (err: any) {
      alert(`Error merging all groups: ${err.message}`);
    } finally {
      setIsMergingAll(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      {/* ... Backdrop ... */}
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-5xl rounded-lg bg-white p-6 shadow-xl dark:bg-darkbglight">
          <Dialog.Title className="text-xl font-bold text-gray-900 dark:text-white flex justify-between items-center">
            Similar City Groups ({isLoading ? '...' : totalGroups} Found)
            <div className="flex items-center gap-4">
              <button
                onClick={handleMergeAll}
                disabled={isLoading || isMergingAll || groups.length === 0}
                className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500 disabled:bg-gray-400"
              >
                {isMergingAll ? 'Merging All...' : 'Merge All Groups'}
              </button>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </Dialog.Title>

          <div className="mt-4 max-h-[75vh] overflow-y-auto pr-4 -mr-4 thin-scroll">
            {/* ... Loading and Error states ... */}
            {isLoading ? (
              <p className="text-center text-gray-500 dark:text-gray-400 py-10">Loading groups...</p>
            ) : error ? (
              <p className="text-center text-red-500 py-10">Error: {error}</p>
            ) : (
              <div className="space-y-6">
                {groups.map((group, index) => (
                  <div key={index} className="rounded-md border border-gray-200 bg-gray-50/50 p-4 dark:border-gray-700 dark:bg-darkbg">
                    <div className="flex justify-between items-start">
                      <h3 className="font-semibold text-lg text-gray-800 dark:text-gray-200 mb-3">
                        Group {index + 1}: <span className="text-entntblue">{group.mostCorrectEntry.name}, {group.mostCorrectEntry.stateName}</span>
                      </h3>
                      <button
                        onClick={() => handleMergeGroup(group)}
                        disabled={isMergingAll || isMerging === group.mostCorrectEntry.id}
                        className="rounded bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-800 shadow-sm hover:bg-red-200 disabled:bg-gray-200 disabled:text-gray-500"
                      >
                        {isMerging === group.mostCorrectEntry.id ? 'Merging...' : 'Merge This Group'}
                      </button>
                    </div>
                    {/* ... Rest of the group display JSX ... */}
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Most Correct Entry */}
                      <div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Suggested Entry to Keep</p>
                        <div className="mt-1 rounded-md border border-green-200 bg-green-100 p-3 text-green-900 dark:border-green-800 dark:bg-green-900/20 dark:text-green-200">
                          <p className="font-bold">{group.mostCorrectEntry.name}</p>
                          <p className="text-xs">{group.mostCorrectEntry.stateName}</p>
                          <p className="text-xs">{group.mostCorrectEntry.id}</p>
                           <span className={`mt-2 inline-block text-xs font-semibold px-2 py-0.5 rounded-full ${group.mostCorrectEntry.isConfirmed ? 'bg-green-200 text-green-900 dark:bg-green-500/50 dark:text-white' : 'bg-yellow-200 text-yellow-900 dark:bg-yellow-500/50 dark:text-white'}`}>
                            {group.mostCorrectEntry.isConfirmed ? 'CONFIRMED' : 'UNCONFIRMED'}
                          </span>
                        </div>
                      </div>

                      {/* Other Entries to Merge */}
                      <div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Entries to Merge ({group.otherEntries.length})</p>
                        <div className="space-y-2 mt-1">
                          {group.otherEntries.map(other => (
                            <div key={other.id} className="rounded-md border border-red-200 bg-red-100 p-3 text-red-900 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
                              <p className="font-bold">{other.name}</p>
                              <p className="text-xs">{other.stateName}</p>
                              <p className="text-xs">{other.id}</p>
                              <div className="mt-2 text-xs font-mono opacity-80">
                                <p className="font-semibold">Dependencies:</p>
                                {other.foundIn.length > 0 ? (
                                  <ul className="list-disc list-inside">
                                    {other.foundIn.map(loc => <li key={loc}>{loc}</li>)}
                                  </ul>
                                ) : (
                                  <p className="italic">None found</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

export default SimilarCityGroupsModal;